import { API_BASE, authHeaders } from './apiAuth'

/**
 * Auto Filter, as the page sees it (workers/api/src/routes/autoFilter.ts).
 * So far the pilot only: pictures already tagged by hand are tagged again by
 * the model, and nothing is written to the library.
 */

export const FILTER_KINDS = ['format', 'material', 'brand', 'product', 'industry'] as const
/** Audit's five, and Type (Sketch or Render), which only Design Works has. */
export type FilterKind = (typeof FILTER_KINDS)[number] | 'type' | 'other'

/**
 * The libraries Auto Filter works on: Audit (five categories), Design Works
 * (Format) and Visual Language (Other, 2026-10-14).
 */
export type FilterLibrary = 'audit' | 'design-works' | 'visual-language'

export interface FilterVocabulary {
  model: string
  groups: Partial<Record<FilterKind, { slug: string; label: string }>>
  tags: Record<FilterKind, { id: string; name: string }[]>
}

export interface PilotKindAnswer {
  tagIds: string[]
  proposed: string[]
  unclear: boolean
  evidence: string
}

export interface PilotResult {
  assetId: string
  title: string
  thumb: string
  /** The tags it already has, by hand, in each of the five categories. */
  manual: Record<FilterKind, string[]>
  answer: {
    isPackaging: boolean
    kinds: Record<FilterKind, PilotKindAnswer>
    usage: { input: number; cached: number; output: number }
    costUsd: number
    ms: number
  } | null
  error: string
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(await authHeaders('web')) },
  })
  if (response.status === 403) throw new Error('Administrators only')
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null
    throw new Error(body?.error ?? `HTTP ${response.status}`)
  }
  return (await response.json()) as T
}

export function readFilterVocabulary(): Promise<FilterVocabulary> {
  return call<FilterVocabulary>('/api/auto-filter/vocabulary')
}

/** Which pictures the pilot tries: already tagged by hand, most categories first. */
export function pickPilot(count: number): Promise<{ assetIds: string[]; available: number }> {
  return call('/api/auto-filter/pilot/pick', { method: 'POST', body: JSON.stringify({ count }) })
}

/** Up to five pictures at once. Nothing is written. */
export async function runPilot(assetIds: string[], detail: 'low' | 'high'): Promise<PilotResult[]> {
  const body = await call<{ results: PilotResult[] }>('/api/auto-filter/pilot/run', {
    method: 'POST',
    body: JSON.stringify({ assetIds, detail }),
  })
  return body.results
}

/* -------------------------------------------------------------------------
   Real runs (2026-10-05): tags are written
   ------------------------------------------------------------------------- */

export interface PendingTag {
  id: string
  name: string
  kind: FilterKind
  count: number
  samples: string[]
}

export interface FilterRunRow {
  id: string
  scope: string
  status: 'running' | 'done' | 'stopped' | 'undone'
  total: number
  done: number
  failed: number
  cost_usd: number | string
  started_by: string
  created_at: string
  finished_at: string | null
  undone_at: string | null
}

export interface FilterOverview {
  model: string
  library: FilterLibrary
  /** The categories it fills in this library. */
  kinds: FilterKind[]
  /** Audit pictures Auto Filter is for: all of them but Non-Packaging. */
  eligible: number
  filtered: number
  failed: number
  thresholds: Record<FilterKind, number>
  pending: PendingTag[]
  tags: Record<FilterKind, { id: string; name: string }[]>
  runs: FilterRunRow[]
  /**
   * False once docs/ai-tags-off.sql has run: tags are written by people, and
   * no run can start (workers/api/src/aiTags.ts). Absent from an older Worker.
   */
  aiTagsOn?: boolean
}

export interface FilterOutcome {
  assetId: string
  status: 'done' | 'skipped' | 'failed'
  costUsd: number
  added: number
  error: string
}

export type RunScope = 'unfiltered' | 'all'

export function readFilterOverview(library: FilterLibrary = 'audit'): Promise<FilterOverview> {
  return call<FilterOverview>(`/api/auto-filter/overview?library=${library}`)
}

export function startFilterRun(scope: RunScope, total: number, library: FilterLibrary = 'audit'): Promise<{ runId: string }> {
  return call('/api/auto-filter/runs/start', { method: 'POST', body: JSON.stringify({ scope, total, library }) })
}

export function nextFilterPictures(
  scope: RunScope,
  after: string,
  count: number,
  library: FilterLibrary = 'audit',
): Promise<{ assetIds: string[]; after: string; end: boolean }> {
  return call('/api/auto-filter/runs/next', { method: 'POST', body: JSON.stringify({ scope, after, count, library }) })
}

/** Up to five pictures, filtered and written. */
export async function processFilterPictures(runId: string, assetIds: string[]): Promise<FilterOutcome[]> {
  const body = await call<{ outcomes: FilterOutcome[] }>('/api/auto-filter/runs/process', {
    method: 'POST',
    body: JSON.stringify({ runId, assetIds }),
  })
  return body.outcomes
}

export function updateFilterRun(
  runId: string,
  progress: { done: number; failed: number; costUsd: number; status?: 'done' | 'stopped' },
): Promise<unknown> {
  return call('/api/auto-filter/runs/update', { method: 'POST', body: JSON.stringify({ runId, ...progress }) })
}

export function undoFilterRun(runId: string): Promise<unknown> {
  return call('/api/auto-filter/runs/undo', { method: 'POST', body: JSON.stringify({ runId }) })
}

export function actOnPendingTag(
  tagId: string,
  action: 'approve' | 'reject' | 'rename' | 'merge',
  extra: { name?: string; intoId?: string } = {},
): Promise<unknown> {
  return call('/api/auto-filter/pending', { method: 'POST', body: JSON.stringify({ tagId, action, ...extra }) })
}

/** Every pending tag of one category joins the library at once. */
export function approveAllPending(kind: FilterKind, library: FilterLibrary = 'audit'): Promise<unknown> {
  return call('/api/auto-filter/pending/approve-all', { method: 'POST', body: JSON.stringify({ kind, library }) })
}

/**
 * "These pictures' tags are wrong" (the red ! beside the selection, website
 * and Miro panel): each Audit picture among them is tagged again by Auto
 * Filter in the background. Anyone signed in may report.
 */
export async function reportPictures(assetIds: string[], library: FilterLibrary = 'audit'): Promise<{ count: number }> {
  const response = await fetch(`${API_BASE}/api/report-pictures`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ assetIds, library }),
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null
    throw new Error(body?.error ?? `HTTP ${response.status}`)
  }
  return (await response.json()) as { count: number }
}

/* =========================================================================
   Picture descriptions (workers/api/src/describe.ts, routes/describe.ts)
   ========================================================================= */

export interface DescribeRun {
  id: string
  library: FilterLibrary
  kind: 'try' | 'all'
  status: 'running' | 'paused' | 'done' | 'stopped'
  total: number
  done: number
  failed: number
  costUsd: number
  startedBy: string
  createdAt: string
  updatedAt: string
  finishedAt: string | null
}

export interface PictureDescription {
  assetId: string
  isPackaging: boolean
  brand: string
  product: string
  variant: string
  format: string
  material: string
  colours: string
  visibleText: string[]
  description: string
  uncertain: string
  model: string
  costUsd: number
  createdAt: string
}

export interface DescribeOverview {
  model: string
  /** Pictures described at the same time. */
  lanes: number
  /** How many a try describes. */
  tryCount: number
  aiTagsOn: boolean
  library: FilterLibrary
  /** The library's pictures (videos and the trash left out). */
  pictures: number
  described: number
  /** What a picture has been costing and taking; from the trial until 5 are measured. */
  perPicture: { usd: number; seconds: number; measured: number }
  /** The run going (running or paused), in whichever library. */
  active: DescribeRun | null
  runs: DescribeRun[]
  latest: (PictureDescription & { thumb: string; full: string })[]
}

export function readDescribeOverview(library: FilterLibrary): Promise<DescribeOverview> {
  return call<DescribeOverview>(`/api/auto-filter/describe/overview?library=${library}`)
}

export function startDescribe(library: FilterLibrary, kind: 'try' | 'all'): Promise<{ runId: string; total: number }> {
  return call('/api/auto-filter/describe/start', { method: 'POST', body: JSON.stringify({ library, kind }) })
}

export function steerDescribe(runId: string, action: 'pause' | 'resume' | 'stop'): Promise<{ ok: boolean }> {
  return call(`/api/auto-filter/describe/${action}`, { method: 'POST', body: JSON.stringify({ runId }) })
}

export function describeAgain(assetId: string): Promise<{ description: PictureDescription | null; costUsd: number }> {
  return call('/api/auto-filter/describe/again', { method: 'POST', body: JSON.stringify({ assetId }) })
}
